/**
 * #1398 — opt-in NBA injury panels beside the two expanded moneyline ladders
 * on Game Detail, and the header control that turns them on.
 *
 * Placement. Each panel sits in the blank strip of its own team's ladder
 * block: from the end of the rung label (`data-ladder-label`) to the start of
 * the rung's own-order area — the reserved gutter (`data-order-gutter`, #1070)
 * for a one-click trader, otherwise the orders cell (`data-ladder-orders`).
 * Nothing in that strip is a control on any row of the block (caption,
 * rungs, spread hairline), so a panel there covers no price, chip, cancel or
 * label. The panels are absolutely positioned in a zero-height anchor after
 * the table, inside the same horizontal scroller: they add no row height, no
 * column width, and they scroll with the ladders. The table's own geometry
 * is unchanged by the panels, and live report updates move no control.
 * (Turning injuries on does add the section's held 28px report line above
 * the table: an explicit layout change, not a live one.)
 *
 * The reserved gutter is never used, even while it is empty: it is the
 * width a first resting order needs, and taking it back when an order arrives
 * would move the panel or the chips. So a one-click trader gets panels only
 * where the table is wide enough to leave room before the gutter; below
 * `PANEL_MIN_WIDTH_PX` a side shows no panel and the section's compact report
 * line (`SectionInjuries`) remains the explicit, bounded way in. A block whose
 * structure cannot be measured (no label, no order area) also fails closed to
 * no panel.
 *
 * #1402 — a section of the ladder, not a card. Each panel spans its block's
 * full height, from the caption row (just under the table header) to the
 * block's last row (the team row below it, or the table end), between two
 * equal faint vertical dividers. It has no rounding, shadow, top or bottom
 * border, or background of its own: it is filled with the ladder rows' own
 * tint, opaque only so the ask/bid hairline does not run through the list.
 * Its header line is the caption row's height and type (the "Resting Qty"
 * style), so the team and its labelled counts read as one more caption. The
 * left divider sits `PANEL_LABEL_CLEARANCE_PX` past the rung label — the
 * gap between two adjacent cells' contents (`Td`'s px-3 on each side), as
 * between the Resting Qty and Resting $ columns — and the right one
 * `PANEL_ORDER_CLEARANCE_PX` before the order area. The section takes the
 * strip up to `PANEL_MAX_WIDTH_PX`, from the left.
 *
 * Re-measured on table or cell resize and when ladder rows mount or unmount,
 * flushed before paint, so a resize does not paint a stale panel over a
 * moved gutter.
 *
 * Report age and the team counts are not repeated: with a team's panel shown
 * the section's report line drops that team's counts (`onPanelTeams`), and
 * the age and exact slot stay on the line alone.
 *
 * #1400 — change cues. A row whose status changed, or a player newly listed
 * by a team that had filed, gets the neutral live-update flash for
 * `INJURY_CUE_MS` (a still tint under reduced motion; background only, so
 * nothing moves). The team header gets the same cue when the team files after
 * Not Yet Submitted or a row leaves its list, since a gone row has nothing to
 * flash (lib/gameState/injuryChanges). Only against a report this panel
 * already showed while the connection was live: the first report, a new game, a report that comes
 * back after retiring and the snapshot after a reconnect are baselines, and
 * age ticks or a republication with the same statuses change nothing.
 *
 * Keyboard: each panel is a `data-lt2-owner` container, so keys inside it
 * never reach the global hotkey listener (no Pull All from Space, no lost aim
 * from Escape); Escape moves focus to the section's Injuries report toggle
 * (itself key-owned), never to the page. The panels are read-only and call
 * nothing in the ticket, the aim or the section's expansion.
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { flushSync } from 'react-dom';
import {
  AlertTriangle, Bandage, CircleCheck, CircleDot, CircleMinus, CircleQuestionMark, CircleX, type LucideIcon,
} from 'lucide-react';
import { coreGameStateKey, parseGameIdentity } from '@/lib/gameState/admission';
import {
  injuryCountTokens, injuryEvidenceWarning, reportAgeWords, sortInjuryRows,
  teamInjuryState, teamInjuryWords, type InjuryStatus, type TeamInjuryState,
} from '@/lib/gameState/injurySignals';
import { injuryChanges, injuryRowId } from '@/lib/gameState/injuryChanges';
import type { InjuryBlock, InjuryPlayer } from '@/lib/gameState/injuries';
import type { GameStateConnectionStatus } from '@/lib/gameState/transport';
import { useSharedNow } from '@/lib/infra/sharedClock';
import { cn } from '@/lib/infra/utils';
import { baseGameKey } from '@/services/analyticsGameKey';
import { useCoreGameStatesStore } from '@/services/coreGameStates';
import { useDisplaySettings } from '@/stores/displaySettings';
import type { TeamSide } from '@/views/ValueDashboard/types';

/** Below this most names would wrap beside their status; the report line
 *  serves instead. #1402: 160 (was 176), so the wider label clearance still
 *  leaves a one-click trader the section where the strip before the gutter
 *  allowed one before. */
export const PANEL_MIN_WIDTH_PX = 160;
/** A wide table does not stretch the list across the whole strip. */
export const PANEL_MAX_WIDTH_PX = 360;
/** Rung label to the left divider: two cells' padding, the gap between
 *  adjacent column contents (e.g. Resting Qty → Resting $). */
export const PANEL_LABEL_CLEARANCE_PX = 24;
/** Right divider to the order area (its chips sit at its far, right edge). */
export const PANEL_ORDER_CLEARANCE_PX = 8;
const PANEL_MIN_HEIGHT_PX = 96;
/** How long a changed row keeps its cue. */
export const INJURY_CUE_MS = 6_000;

const SIDES: readonly TeamSide[] = ['away', 'home'];

/** Core's key for a board/route game key, when it is an NBA game. */
function nbaInjuryKey(gameKey: string | null | undefined): string | null {
  const key = gameKey ? coreGameStateKey(baseGameKey(gameKey)) : null;
  return key && parseGameIdentity(key)?.sport === 'nba' ? key : null;
}

/**
 * Whether this game shows injury surfaces at all: injury data is configured,
 * the game is NBA, and the reader turned the panels on.
 */
export function useInjuryPanelsEnabled(gameKey: string | null | undefined): boolean {
  const on = useDisplaySettings((s) => s.showInjuryPanels);
  const configured = useCoreGameStatesStore((s) => s.configured);
  return on && configured && nbaInjuryKey(gameKey) != null;
}

/**
 * The Game Detail header control. A display preference only (remembered with
 * the other display settings): it is shown to every reader of an NBA game and
 * reads or changes no execution grant.
 */
export function InjuryPanelsToggle({ gameKey }: { gameKey: string | null | undefined }) {
  const on = useDisplaySettings((s) => s.showInjuryPanels);
  const setOn = useDisplaySettings((s) => s.setShowInjuryPanels);
  const configured = useCoreGameStatesStore((s) => s.configured);
  if (!configured || nbaInjuryKey(gameKey) == null) return null;
  return (
    <button
      type="button"
      data-lt2-owner=""
      data-testid="injury-panels-toggle"
      aria-pressed={on}
      onClick={() => setOn(!on)}
      title={on ? 'Hide the injury panels and report line' : 'Show injury panels beside the expanded moneyline ladders (display only)'}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider',
        on ? 'border-primary/50 bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:text-foreground',
      )}
    >
      <Bandage size={13} aria-hidden />
      Injuries
    </button>
  );
}

export interface PanelBox {
  top: number; left: number; width: number; height: number;
  /** The caption row's height: the section's header line matches it. */
  caption: number;
}
/** A side's measurement: a box, too little room, or no ladder block shown. */
export type PanelFit = { kind: 'panel'; box: PanelBox } | { kind: 'narrow' } | { kind: 'absent' };

/**
 * Pure geometry for one side, in the anchor's coordinates (exported for the
 * placement tests). A block with no label or no order area fails closed.
 */
export function measureLadderBlock(table: HTMLElement, anchor: HTMLElement, side: TeamSide): PanelFit {
  const rows = table.querySelectorAll<HTMLElement>(`tr[data-ladder-block="${side}"]`);
  if (rows.length === 0) return { kind: 'absent' };
  const labels = table.querySelectorAll<HTMLElement>(`tr[data-ladder-block="${side}"] [data-ladder-label]`);
  const gutters = table.querySelectorAll<HTMLElement>(`tr[data-ladder-block="${side}"] [data-order-gutter]`);
  const orderCells = table.querySelectorAll<HTMLElement>(`tr[data-ladder-block="${side}"] [data-ladder-orders]`);
  const stops = gutters.length > 0 ? gutters : orderCells;
  if (labels.length === 0 || stops.length === 0) return { kind: 'narrow' };
  let start = -Infinity;
  for (const el of labels) start = Math.max(start, el.getBoundingClientRect().right);
  let end = Infinity;
  for (const el of stops) end = Math.min(end, el.getBoundingClientRect().left);
  const a = anchor.getBoundingClientRect();
  const first = rows[0].getBoundingClientRect();
  const bottom = rows[rows.length - 1].getBoundingClientRect().bottom;
  // Whole pixels inward on both sides.
  const left = Math.ceil(start + PANEL_LABEL_CLEARANCE_PX - a.left);
  const room = Math.floor(end - PANEL_ORDER_CLEARANCE_PX - a.left) - left;
  const height = bottom - first.top;
  if (!Number.isFinite(room) || room < PANEL_MIN_WIDTH_PX || height < PANEL_MIN_HEIGHT_PX) return { kind: 'narrow' };
  return {
    kind: 'panel',
    box: {
      top: Math.round(first.top - a.top),
      left,
      width: Math.min(room, PANEL_MAX_WIDTH_PX),
      height: Math.round(bottom - a.top) - Math.round(first.top - a.top),
      caption: Math.round(first.bottom - first.top),
    },
  };
}

const sameFit = (x: PanelFit, y: PanelFit) => x.kind === y.kind
  && (x.kind !== 'panel' || y.kind !== 'panel'
    || (['top', 'left', 'width', 'height', 'caption'] as const).every((k) => x.box[k] === y.box[k]));

type Fits = Record<TeamSide, PanelFit>;
const ABSENT: Fits = { away: { kind: 'absent' }, home: { kind: 'absent' } };

/** What to cue, each with its animation phase (see `cueClass`). */
export interface InjuryCues {
  rows: ReadonlyMap<string, number>;
  teams: ReadonlyMap<string, number>;
}
const NO_CUES: InjuryCues = { rows: new Map(), teams: new Map() };

/** Each id's phase flips from its own active cue (0 when it has none), so its
 *  next change always swaps animation names — whatever else changed between. */
function withCue(into: ReadonlyMap<string, number>, ids: ReadonlySet<string>): Map<string, number> {
  const out = new Map(into);
  for (const id of ids) {
    const was = into.get(id);
    out.set(id, was === undefined ? 0 : 1 - was);
  }
  return out;
}

/** The phase's animation name: a swap restarts the fade on the same element. */
const cueClass = (phase: number | undefined) =>
  phase === undefined ? undefined : phase ? 'animate-injury-cue-alt' : 'animate-injury-cue';

/**
 * The rows and team headers to cue (see the header): changes between reports
 * of one game (`gameKey`) this panel showed while the connection was live.
 * Leaving `live`, a retired report or another game — even one sharing a team
 * — drops the baseline AND every active cue with its timer; the first live
 * report after that is the new baseline, so neither a reconnect snapshot nor
 * a new game cues anything, and no old cue survives into it. Cues from
 * successive updates merge and all end `INJURY_CUE_MS` after the latest.
 */
export function useInjuryRowChanges(gameKey: string | null, block: InjuryBlock | null, status: GameStateConnectionStatus): InjuryCues {
  const base = useRef<{ gameKey: string | null; block: InjuryBlock } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
   const [cues, setCues] = useState<InjuryCues>(NO_CUES);
  useEffect(() => {
    const prev = base.current?.gameKey === gameKey ? base.current.block : null;
    if (status !== 'live' || !block || !prev) {
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
      setCues(NO_CUES);
    }
    base.current = status === 'live' && block ? { gameKey, block } : null;
    if (!base.current || !prev || prev === block) return;
    const changed = injuryChanges(prev, block!);
    if (changed.rows.size === 0 && changed.teams.size === 0) return;
    setCues((c) => ({ rows: withCue(c.rows, changed.rows), teams: withCue(c.teams, changed.teams) }));
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; setCues(NO_CUES); }, INJURY_CUE_MS);
  }, [gameKey, block, status]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return cues;
}

/**
 * The two team panels for the moneyline table this is rendered AFTER (its
 * previous sibling). `teams` gives each side's code and name; `onPanelTeams`
 * hears the codes that have a panel drawn (empty when none, and on unmount).
 */
export function InjuryLadderPanels({ gameKey, teams, onPanelTeams }: {
  gameKey: string;
  teams: Record<TeamSide, { code: string; name: string }>;
  onPanelTeams?: (codes: readonly string[]) => void;
}) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [fits, setFits] = useState<Fits>(ABSENT);
  const key = nbaInjuryKey(gameKey);
  const row = useCoreGameStatesStore((s) => (key ? s.injuries.get(key) : undefined));
  const status = useCoreGameStatesStore((s) => s.status);
  const now = useSharedNow(30_000);
  const block = row?.block ?? null;
  const changed = useInjuryRowChanges(key, block, status);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const table = anchor?.previousElementSibling as HTMLElement | null;
    if (!anchor || !table || table.tagName !== 'TABLE') return;
    const measure = () => {
      const next: Fits = { away: measureLadderBlock(table, anchor, 'away'), home: measureLadderBlock(table, anchor, 'home') };
      setFits((prev) => (sameFit(prev.away, next.away) && sameFit(prev.home, next.home) ? prev : next));
    };
    // The cells left of the prices: their widths move the label end and the
    // order area without necessarily resizing the table.
    const ro = new ResizeObserver(() => flushSync(measure));
    const observeCells = () => {
      ro.disconnect();
      ro.observe(table);
      for (const side of SIDES) {
        const first = table.querySelector<HTMLElement>(`tr[data-ladder-block="${side}"] [data-ladder-label]`)?.closest('tr');
        Array.from(first?.cells ?? []).slice(0, 3).forEach((td) => ro.observe(td));
      }
    };
    observeCells();
    measure();
    const tbody = table.querySelector('tbody');
    const mo = new MutationObserver(() => { observeCells(); flushSync(measure); });
    if (tbody) mo.observe(tbody, { childList: true });
    return () => { ro.disconnect(); mo.disconnect(); };
  }, []);

  // Before paint, so the report line never shows counts a panel also shows.
  const notify = useRef(onPanelTeams);
  useLayoutEffect(() => { notify.current = onPanelTeams; });
  const shown = SIDES.filter((side) => fits[side].kind === 'panel').map((side) => teams[side].code).join(' ');
  useLayoutEffect(() => { notify.current?.(shown ? shown.split(' ') : []); }, [shown]);
  useLayoutEffect(() => () => notify.current?.([]), []);

  const warning = block ? injuryEvidenceWarning(row, status, now) : null;
  return (
    <div
      ref={anchorRef}
      className="relative h-0"
      data-testid="injury-ladder-panels"
      data-fit={`away:${fits.away.kind} home:${fits.home.kind}`}
    >
      {SIDES.map((side) => {
        const fit = fits[side];
        if (fit.kind !== 'panel') return null;
        return (
          <TeamInjuryPanel
            key={side}
            style={{ position: 'absolute', top: fit.box.top, left: fit.box.left, width: fit.box.width, height: fit.box.height }}
            caption={fit.box.caption}
            code={teams[side].code}
            name={teams[side].name}
            block={block}
            lastKnown={warning}
            now={now}
            rowCues={changed.rows}
            teamCue={changed.teams.get(teams[side].code)}
          />
        );
      })}
    </div>
  );
}

const TONE: Record<InjuryStatus, string> = {
  out: 'text-red-400 light:text-red-600',
  doubtful: 'text-red-400 light:text-red-600',
  questionable: 'text-amber-400 light:text-amber-700',
  probable: 'text-muted-foreground',
  available: 'text-muted-foreground',
};

/** Real status marks: the list's icon column and the legend that explains them.
 *  A row's status is its mark (named for assistive technology), not a word. */
const STATUS_ICON: Record<InjuryStatus, LucideIcon> = {
  out: CircleX,
  doubtful: CircleMinus,
  questionable: CircleQuestionMark,
  probable: CircleDot,
  available: CircleCheck,
};

const STATUS_LABEL: Record<InjuryStatus, string> = {
  out: 'Out', doubtful: 'Doubtful', questionable: 'Questionable', probable: 'Probable', available: 'Available',
};

/** The caption-row type of the ladder ("Resting Qty"): the header matches it. */
const CAPTION_TYPE = 'text-[9px] font-semibold uppercase tracking-wide';
/** The header's line box (`leading-3`). */
const CAPTION_LINE_PX = 12;

/**
 * The header's state: labelled counts, each led by its status mark —
 * `⊗ 04 Out  ? 02 Questionable`, mark and number in the status colour — so
 * the legend is what explains the list's marks. Never abbreviated or
 * clipped: tokens wrap whole onto further caption lines when the section is
 * narrow. Otherwise the team's report state.
 */
function HeaderLegend({ state }: { state: TeamInjuryState }) {
  if (state.kind === 'unavailable') return <span>no report</span>;
  if (state.kind === 'pending') return <span className="text-amber-400 light:text-amber-700">not yet submitted</span>;
  if (state.kind === 'clean') return <span>none listed</span>;
  return <>
    {injuryCountTokens(state.counts).map((t) => {
      const Icon = STATUS_ICON[t.status];
      return (
        <span key={t.status} className="inline-flex items-center gap-1 whitespace-nowrap" data-testid="injury-legend-token" data-status={t.status}>
          <Icon size={10} aria-hidden className={cn('shrink-0', TONE[t.status])} />
          <span className={cn('font-mono tabular-nums', TONE[t.status])}>{String(t.count).padStart(2, '0')}</span>
          {STATUS_LABEL[t.status]}
        </span>
      );
    })}
  </>;
}

function EmptyLine({ state }: { state: TeamInjuryState }) {
  // Pending ("Not Yet Submitted") and filed-empty are different reports.
  const text = state.kind === 'unavailable' ? 'No injury report for this team.'
    : state.kind === 'pending' ? 'Report not yet submitted.'
      : 'Filed · no players listed.';
  return <p className="text-muted-foreground px-3 py-1.5">{text}</p>;
}

/** Rows in display order, keyed by identity (with an occurrence count for a
 *  same-name pair), not position: a status change re-sorts the list, and a
 *  positional key would remount the moved rows. */
function keyedRows(players: readonly InjuryPlayer[]): { p: InjuryPlayer; key: string }[] {
  const seen = new Map<string, number>();
  return sortInjuryRows(players).map((p) => {
    const id = injuryRowId(p);
    const n = seen.get(id) ?? 0;
    seen.set(id, n + 1);
    return { p, key: `${id}#${n}` };
  });
}

/** One team's section: a caption-aligned header and its scrolling player list. */
export function TeamInjuryPanel({ style, caption, code, name, block, lastKnown, now, rowCues = NO_CUES.rows, teamCue }: {
  style?: CSSProperties;
  /** The ladder caption row's height, for the header's first line (default 20). */
  caption?: number;
  code: string;
  name: string;
  block: InjuryBlock | null;
  /** The last-known warning text, when the shown block is not current. */
  lastKnown: string | null;
  now: number;
  /** Row ids (`injuryRowId`) to cue as just changed, with their phase. */
  rowCues?: ReadonlyMap<string, number>;
  /** The header's cue phase: the team just filed, or a row left its list. */
  teamCue?: number;
}) {
  const state = teamInjuryState(block, code);
  const players = block?.teams[code]?.players ?? [];
  // The header's first line is the caption row: its height, its type, its
  // vertical centre (a 12px line box). Further legend lines follow beneath.
  const captionPx = caption ?? 20;
  const padY = Math.max(0, (captionPx - CAPTION_LINE_PX) / 2);
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Escape') return;
    // Answered here, never by the board: Escape must not clear the aim. Focus
    // goes to an injury-owned control — the section's report toggle, else
    // this panel — never to the page, where the next Space is Pull All.
    e.stopPropagation();
    e.preventDefault();
    const panel = e.currentTarget;
    const toggle = panel.closest('[data-view-anchor]')
      ?.querySelector<HTMLElement>('[data-testid="section-injuries"] button[aria-expanded]');
    (toggle ?? panel).focus();
  };
  return (
    <section
      style={style}
      aria-label={`${name || code} injuries`}
      data-lt2-owner=""
      data-testid="team-injury-panel"
      data-team={code}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      // The ladder's own colours: the card under the table, the rows' tint
      // over it, and the hairline's faint border for the two dividers. The
      // card colour by value, not `.bg-card`: light mode gives every
      // `.bg-card` a shadow (globals.css), and this is not a card.
      className="min-w-0 bg-[hsl(var(--card))] text-[11px] leading-4"
    >
      <div className="border-border/40 flex h-full min-w-0 flex-col border-x bg-black/15 light:bg-black/[0.08]">
        {/* The header is one more caption: the caption row's height and type.
            On last-known data an amber warning closes it, and the report line
            above says why. */}
        <div
          data-testid="team-injury-header"
          data-changed={teamCue !== undefined || undefined}
          style={{ minHeight: captionPx, paddingTop: padY, paddingBottom: padY }}
          className={cn(
            'text-muted-foreground/70 flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 px-3',
            CAPTION_TYPE,
            // After the type: tailwind-merge drops a line height that precedes a font size.
            'leading-3',
            cueClass(teamCue),
          )}
          title={block ? `${name || code} · ${teamInjuryWords(state)} · report ${reportAgeWords(block.reportAt, now)}${lastKnown ? ` · ${lastKnown}` : ''}` : undefined}
        >
          <span className="text-muted-foreground" data-testid="team-injury-code">{code}</span>
          <HeaderLegend state={state} />
          {/* At the end, so going last-known moves neither the team nor the legend. */}
          {lastKnown && <AlertTriangle size={12} role="img" aria-label={lastKnown} data-testid="injury-last-known" className="ml-auto shrink-0 text-amber-500 light:text-amber-700" />}
          {teamCue !== undefined && <span className="sr-only"> (report updated)</span>}
        </div>
        {/* A status mark in its own narrow column, on the name's line; the
            name with its reason beneath. The status itself is the mark's
            accessible name and title, explained by the header's legend — not
            repeated as a word on every row. Exact status-since times and
            prop-linkage limits stay in the full report. Scrolls within the
            block. */}
        <div
          role="region"
          aria-label={`${code} injury report`}
          tabIndex={0}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain break-words [overflow-wrap:anywhere]"
        >
          {state.kind !== 'listed' ? <EmptyLine state={state} /> : (
            <ul className="divide-border/40 divide-y">
              {keyedRows(players).map(({ p, key }) => {
                // Ambiguous same-name rows are never cued (injuryChanges).
                const cue = rowCues.get(injuryRowId(p));
                const Icon = STATUS_ICON[p.status];
                return (
                  <li
                    key={key}
                    data-player-key={p.playerKey}
                    data-changed={cue !== undefined || undefined}
                    className={cn('grid grid-cols-[12px_minmax(0,1fr)] gap-x-2 px-3 py-1.5', cueClass(cue))}
                  >
                    <span className="flex h-4 items-center" title={p.statusRaw}>
                      <Icon size={12} role="img" aria-label={p.statusRaw} data-testid="injury-status-icon" data-status={p.status} className={TONE[p.status]} />
                    </span>
                    <span className="min-w-0 font-medium" data-testid="injury-player">
                      {p.player}
                      {cue !== undefined && <span className="sr-only"> (updated)</span>}
                    </span>
                    {p.reason && <p className="text-muted-foreground col-start-2 line-clamp-2 text-[10px]" title={p.reason}>{p.reason}</p>}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
